// Drag-and-drop upload zone for reference images. Supports multiple
// files at once, falls back to a hidden file input via the "browse"
// link. Each file goes through the same three-step SAS dance as the
// project PDF upload (mint → PUT → register).
import { useRef, useState } from "react";
import { Upload } from "lucide-react";
import { api } from "../../../lib/api";
import type { ReferenceImageDto } from "./ReferenceLibrary";

const ACCEPTED = ["image/png", "image/jpeg", "image/webp"];

interface Props {
  typeId: number;
  onUploaded: (img: ReferenceImageDto) => void;
}

async function uploadOne(typeId: number, file: File): Promise<ReferenceImageDto> {
  const sas = await api<{ blobPath: string; uploadUrl: string }>(
    "/api/tools/pipe-takeoff/references/sas-url",
    { method: "POST", json: { filename: file.name, contentType: file.type || "image/png" } },
  );
  const putRes = await fetch(sas.uploadUrl, {
    method: "PUT",
    headers: {
      "x-ms-blob-type": "BlockBlob",
      "Content-Type": file.type || "image/png",
    },
    body: file,
  });
  if (!putRes.ok) {
    throw new Error(`Blob upload failed: ${putRes.status} ${putRes.statusText}`);
  }
  return api<ReferenceImageDto>(
    `/api/tools/pipe-takeoff/references/${typeId}/images`,
    {
      method: "POST",
      json: { blobPath: sas.blobPath, filename: file.name, caption: null },
    },
  );
}

export function ReferenceImageDropzone({ typeId, onUploaded }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [active, setActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFiles = async (files: FileList | File[]) => {
    setError(null);
    const arr = Array.from(files).filter((f) => {
      if (!ACCEPTED.includes(f.type)) {
        setError(`Skipped ${f.name}: unsupported type ${f.type || "(unknown)"}`);
        return false;
      }
      return true;
    });
    if (arr.length === 0) return;
    setBusy(true);
    try {
      for (const file of arr) {
        const img = await uploadOne(typeId, file);
        onUploaded(img);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setActive(true); }}
      onDragLeave={() => setActive(false)}
      onDrop={(e) => {
        e.preventDefault();
        setActive(false);
        if (e.dataTransfer.files.length > 0) void handleFiles(e.dataTransfer.files);
      }}
      style={{
        border: "1.5px dashed var(--bsp-border-strong)",
        borderColor: active ? "var(--bsp-blue)" : "var(--bsp-border-strong)",
        background: active ? "var(--bsp-blue-pale)" : "var(--bsp-surface)",
        borderRadius: 6,
        padding: "20px 16px",
        textAlign: "center",
        transition: "border-color var(--dur-fast) var(--ease-standard), background var(--dur-fast) var(--ease-standard)",
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED.join(",")}
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files && e.target.files.length > 0) void handleFiles(e.target.files);
        }}
      />
      <Upload size={20} style={{ color: "var(--bsp-fg-muted)", marginBottom: 8 }} />
      <div style={{ fontSize: 13 }}>
        {busy ? (
          "Uploading…"
        ) : (
          <>
            Drag &amp; drop images here, or{" "}
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              style={{
                background: "transparent", border: 0, padding: 0,
                color: "var(--bsp-blue)", textDecoration: "underline",
                cursor: "pointer", font: "inherit",
              }}
            >
              browse
            </button>
          </>
        )}
      </div>
      <div style={{ fontSize: 11, color: "var(--bsp-fg-muted)", marginTop: 4 }}>
        PNG, JPG, WEBP — highlight components in yellow for best results
      </div>
      {error && (
        <p style={{ color: "var(--bsp-red-fg)", fontSize: 12, marginTop: 8, marginBottom: 0 }}>
          {error}
        </p>
      )}
    </div>
  );
}
